/**
 * @file CalendarHolidaysTab.tsx
 * @brief Admin settings tab for company-wide public holidays shown on every calendar.
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Plus, Pencil, Trash2, CalendarDays } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { calendarService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import type { CalendarEvent } from '@/types';

const CalendarHolidaysTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [editing, setEditing] = useState<CalendarEvent | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDate, setEditDate] = useState('');
  const [deleting, setDeleting] = useState<CalendarEvent | null>(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.holidays });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.calendarEvents });
  };

  const { data: holidays = [], isLoading, error } = useQuery({
    queryKey: QUERY_KEYS.holidays,
    queryFn: () => calendarService.getHolidays(),
  });

  const createMutation = useMutation({
    mutationFn: () => calendarService.createHoliday({ title: title.trim(), date }),
    onSuccess: () => {
      invalidate();
      setTitle('');
      setDate('');
    },
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      calendarService.updateHoliday(editing!.id, { title: editTitle.trim(), date: editDate }),
    onSuccess: () => {
      invalidate();
      setEditing(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => calendarService.deleteHoliday(id),
    onSuccess: () => {
      invalidate();
      setDeleting(null);
    },
  });

  const sorted = [...holidays].sort((a, b) =>
    dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf()
  );

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <CalendarDays size={18} />
        <Typography variant="h6" fontWeight={600}>
          Public holidays
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Holidays added here appear on every user’s calendar.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3, alignItems: 'flex-start' }}>
        <TextField
          label="Holiday name"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          size="small"
          sx={{ minWidth: 220, flex: 1 }}
        />
        <TextField
          label="Date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 180 }}
        />
        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          disabled={!title.trim() || !date || createMutation.isPending}
          onClick={() => createMutation.mutate()}
          sx={{ minHeight: 40 }}
        >
          Add
        </Button>
      </Box>

      {(createMutation.isError || error) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(createMutation.error as Error)?.message
            || (error as Error)?.message
            || 'Could not load holidays'}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : sorted.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No holidays yet. Add one above.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Holiday</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((holiday) => (
                <TableRow key={holiday.id} hover>
                  <TableCell>{dayjs(holiday.startDate).format('D MMM YYYY')}</TableCell>
                  <TableCell>{holiday.title}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      aria-label="Edit holiday"
                      onClick={() => {
                        setEditing(holiday);
                        setEditTitle(holiday.title);
                        setEditDate(dayjs(holiday.startDate).format('YYYY-MM-DD'));
                      }}
                    >
                      <Pencil size={16} />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label="Delete holiday"
                      onClick={() => setDeleting(holiday)}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit holiday</DialogTitle>
        <DialogContent>
          {updateMutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {(updateMutation.error as Error)?.message || 'Update failed'}
            </Alert>
          )}
          <TextField
            label="Holiday name"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            fullWidth
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            label="Date"
            type="date"
            value={editDate}
            onChange={(e) => setEditDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={updateMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => updateMutation.mutate()}
            disabled={!editTitle.trim() || !editDate || updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleting} onClose={() => setDeleting(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete holiday</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove {deleting?.title} from all calendars?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={deleteMutation.isPending}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleting && deleteMutation.mutate(deleting.id)}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CalendarHolidaysTab;
